import { TNewOrderForm } from "@/entities/new-order";
import { TReviewValue } from "@/entities/new-order/types/new-order-keywords.type";
import { getAppStore } from "@/shared/helpers";
import { useGenerateReviewsMutation } from "@/Store/APIs/Campaign.api";
import BasicRadioRow from "@/UI/BasicRadioRow";
import { throwNewToastError } from "@/Utils/MainUtils";
import { Flex, Skeleton } from "@mantine/core";
import { IconCalendarWeek } from "@tabler/icons-react";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import CountryReviewItem from "./country-review-item";
import styles from "./country-review.module.scss";

interface CountryReviewDateItemProps {
  currentIndex: number;
  groupId: string;
  date: string;
  replace?: (value: TNewOrderForm["countriesList"]) => void;
  update: (
    index: number,
    value: TNewOrderForm["countriesList"][number],
  ) => void;
}

function CountryReviewDateItem({
  currentIndex,
  groupId,
  date,
  update,
}: CountryReviewDateItemProps) {
  const [generateRate, setGenerateRate] = useState<"4" | "5">("5");
  const [countGeneratedReviews, setCountGeneratedReviews] = useState("");
  const radioName = `country-review-rate-${groupId}`;
  const { watch, setValue } = useFormContext<TNewOrderForm>();
  const [generateReviews, { isLoading }] = useGenerateReviewsMutation();

  const { countriesList, app } = watch();

  const country = countriesList[currentIndex];

  const handleGenerateReviews = async () => {
    if (isLoading) return;

    if (!countGeneratedReviews) {
      throwNewToastError(
        "Please enter the number of reviews to generate (Max: 10).",
      );
      return;
    }

    if (Number(countGeneratedReviews) > 10) {
      throwNewToastError("Maximum count of reviews for generation is 10.");
      return;
    }

    if (!country.geo) {
      throwNewToastError("Please choose the country for generating reviews.");
      return;
    }

    if (!app) {
      throwNewToastError("Please choose the app for generating reviews.");
      return;
    }

    try {
      const response = await generateReviews({
        country: country.geo,
        count: Number(countGeneratedReviews),
        isNeedTitle: getAppStore(app.url) === "apple",
        appId: app.id || undefined,
      }).unwrap();

      const dateReviews = country.keywords[date];
      if (!Array.isArray(dateReviews)) {
        return;
      }

      const safeDateReviews: TReviewValue[] = [...dateReviews];

      response.forEach((review) => {
        safeDateReviews.unshift({
          id: `${Date.now()}-${safeDateReviews.length}`,
          review: review.text,
          title: review.title,
          rating: generateRate === "4" ? 4 : 5,
        });
      });

      setValue(
        `countriesList.${currentIndex}.keywords.${date}` as const,
        safeDateReviews,
        {
          shouldDirty: true,
          shouldValidate: true,
        },
      );
    } catch (err) {
      console.error(err);
    }
  };

  const handleInputCountReviews = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digitsOnly = e.target.value.replace(/\D/g, "");
    let normalized = digitsOnly.replace(/^0+/, "");

    if (Number(normalized) > 10) {
      normalized = "10";
    }

    setCountGeneratedReviews(normalized);
  };

  const handleAddManuallyReview = () => {
    if (!country) {
      return;
    }

    const dateReviews = country.keywords[date];
    if (!Array.isArray(dateReviews)) {
      return;
    }

    const safeDateReviews: TReviewValue[] = [...dateReviews];

    safeDateReviews.push({
      id: `${Date.now()}-${safeDateReviews.length}`,
      review: "",
      title: "",
      rating: 5,
    });

    const nextCountriesList = [...countriesList];
    nextCountriesList[currentIndex] = {
      ...country,
      keywords: {
        ...(country.keywords as Record<string, TReviewValue[]>),
        [date]: safeDateReviews,
      },
    };

    update(currentIndex, {
      ...country,
      keywords: {
        ...(country.keywords as Record<string, TReviewValue[]>),
        [date]: safeDateReviews,
      },
    });
  };

  return (
    <Flex direction="column" className={styles.cardContainer} gap="xl">
      <Flex align="center" gap="lg">
        <Flex align="center" gap="md">
          <IconCalendarWeek color="var(--color-grey-semi)" />
          <p className={styles.cardDate}>{date}</p>
        </Flex>
        <Flex align="center" gap="md">
          <BasicRadioRow
            value={`${groupId}-4`}
            name={radioName}
            text="4-Star"
            onChange={() => setGenerateRate("4")}
            checked={generateRate === "4"}
          />
          <BasicRadioRow
            value={`${groupId}-5`}
            name={radioName}
            text="5-Star"
            onChange={() => setGenerateRate("5")}
            checked={generateRate === "5"}
          />
          <div className={styles.generateInputBlock}>
            <input
              type="text"
              inputMode="numeric"
              pattern="[1-9][0-9]*"
              onChange={handleInputCountReviews}
              value={countGeneratedReviews}
              className={styles.reviewsCountInput}
              placeholder="Count of reviews"
            />
            <button
              className={styles.generateButton}
              onClick={handleGenerateReviews}
              disabled={isLoading}
            >
              Generate
            </button>
          </div>
        </Flex>
        <button
          className={styles.addManuallyButton}
          onClick={handleAddManuallyReview}
        >
          Add manually
        </button>
      </Flex>
      {isLoading && (
        <Flex direction="column" gap="xl">
          {Array.from({ length: Number(countGeneratedReviews) }).map(
            (_, index) => (
              <SkeletonReviewItem key={index} />
            ),
          )}
        </Flex>
      )}
      {Array.isArray(country.keywords[date]) &&
        (country.keywords[date] as TReviewValue[]).length > 0 && (
          <Flex direction="column" gap="xl">
            {(country.keywords[date] as TReviewValue[]).map((review, index) => (
              <CountryReviewItem
                key={review.id}
                index={index}
                review={review}
                currentIndex={currentIndex}
                date={date}
              />
            ))}
          </Flex>
        )}
    </Flex>
  );
}

export default CountryReviewDateItem;

function SkeletonReviewItem() {
  return (
    <Flex direction="column" gap="xs">
      <Flex align="center" justify="space-between">
        <Skeleton height={20} width="14%" radius="xl" />
        <Skeleton height={20} width="20%" radius="xl" />
        <Skeleton height={35} width="32%" />
      </Flex>
      <Skeleton height={35} width="100%" />
    </Flex>
  );
}
